import { useState, useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { runAnalysis, generateMarkdownReport } from './benchmark-utils'
import { format } from 'date-fns'
import { Badge } from '@/components/ui/badge'
import { BarChart3, Settings2, Target, Percent, Calendar, Filter } from 'lucide-react'

const TARGET_DAY_OPTIONS = [60, 90, 120, 150, 180]

export function BenchmarkView({ dailyData, allDailyData, sourceInfo = { appName: 'Analysis' } }) {
  const [targetDay, setTargetDay] = useState(90)
  const [threshold, setThreshold] = useState(100)
  const [excludeBefore, setExcludeBefore] = useState('2025-03-01')
  const [excludeRangesStr, setExcludeRangesStr] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  const dataSource = allDailyData ?? dailyData ?? []

  const excludeRanges = useMemo(() => {
    if (!excludeRangesStr?.trim()) return []
    return excludeRangesStr
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter((s) => /^\d{4}-\d{2}-\d{2}:\d{4}-\d{2}-\d{2}$/.test(s))
  }, [excludeRangesStr])

  // 当面板打开时才进行计算
  const analysis = useMemo(() => {
    if (!isOpen || !dataSource?.length) return null
    return runAnalysis(
      dataSource,
      threshold,
      targetDay,
      excludeBefore || null,
      excludeRanges
    )
  }, [isOpen, dataSource, threshold, targetDay, excludeBefore, excludeRanges])

  const markdownReport = useMemo(() => {
    if (!analysis) return ''
    return generateMarkdownReport(analysis, threshold, targetDay, sourceInfo)
  }, [analysis, threshold, targetDay, sourceInfo])

  const handleCopyMarkdown = async () => {
    if (!markdownReport) return
    try {
      await navigator.clipboard.writeText(markdownReport)
      alert('Markdown 报告已复制到剪贴板')
    } catch (err) {
      console.error('Copy failed:', err)
      alert('复制失败，请手动复制')
    }
  }

  if (!dataSource?.length) {
    return null
  }

  return (
    <>
      <Button
        variant="default"
        size="sm"
        className="gap-2"
        onClick={() => setIsOpen(true)}
      >
        <BarChart3 className="h-4 w-4" />
        Benchmark分析
      </Button>

      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent
          side="right"
          className="w-[90vw] sm:w-[800px] lg:w-[1000px] xl:w-[1200px] max-w-none p-0 flex flex-col"
        >
          {/* 顶部固定区域：标题、状态、参数 */}
          <div className="border-b shrink-0">
            <SheetHeader className="px-6 py-4">
              <SheetTitle className="flex items-center gap-2 text-xl">
                <BarChart3 className="h-6 w-6 text-primary" />
                ROAS 基准分析报告
              </SheetTitle>
              <SheetDescription className="flex flex-wrap gap-2 mt-2">
                <Badge variant="secondary" className="bg-primary/10 text-primary hover:bg-primary/20 border-none font-medium px-2 py-0.5">
                  <Target className="w-3.5 h-3.5 mr-1" />
                  目标: Day {targetDay} ROAS ≥ {threshold}%
                </Badge>
                <Badge variant="outline" className="text-muted-foreground font-normal bg-background px-2 py-0.5">
                  <Calendar className="w-3.5 h-3.5 mr-1" />
                  日期: {analysis?.dateMin ? format(analysis.dateMin, 'yyyy-MM-dd') : '–'} ~ {analysis?.dateMax ? format(analysis.dateMax, 'yyyy-MM-dd') : '–'}
                </Badge>
                <Badge variant="outline" className="text-muted-foreground font-normal bg-background px-2 py-0.5">
                  <Filter className="w-3.5 h-3.5 mr-1" />
                  队列数: {analysis?.cohorts?.length ?? 0}
                </Badge>
                {analysis?.excludeDesc && analysis.excludeDesc !== '–' && (
                  <Badge variant="destructive" className="bg-red-50 text-red-600 hover:bg-red-100 border-none font-normal text-xs px-2 py-0.5">
                    排除规则已生效
                  </Badge>
                )}
              </SheetDescription>
            </SheetHeader>

            {/* 优化后的参数选择区域 */}
            <div className="px-6 py-5 bg-muted/20 border-t">
              <div className="flex items-center gap-2 mb-4 text-sm font-medium text-foreground/70">
                <Settings2 className="w-4 h-4" />
                分析参数配置
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="space-y-1.5">
                  <Label htmlFor="target-day" className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                    <Target className="w-3 h-3" />
                    目标天数
                  </Label>
                  <Select
                    value={String(targetDay)}
                    onValueChange={(v) => setTargetDay(Number(v))}
                  >
                    <SelectTrigger id="target-day" className="h-9 bg-background shadow-sm border-muted-foreground/20 focus:ring-primary/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TARGET_DAY_OPTIONS.map((d) => (
                        <SelectItem key={d} value={String(d)}>
                          Day {d}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="threshold" className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                    <Percent className="w-3 h-3" />
                    达标阈值 (%)
                  </Label>
                  <div className="relative">
                    <Input
                      id="threshold"
                      type="number"
                      min={0}
                      step={1}
                      value={threshold}
                      onChange={(e) => {
                        const value = Number(e.target.value)
                        setThreshold(Number.isNaN(value) ? 100 : value)
                      }}
                      className="h-9 bg-background shadow-sm border-muted-foreground/20 pr-8 focus:ring-primary/20"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs font-medium">%</span>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="exclude-before" className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                    <Calendar className="w-3 h-3" />
                    排除日期之前
                  </Label>
                  <Input
                    id="exclude-before"
                    type="date"
                    value={excludeBefore}
                    onChange={(e) => setExcludeBefore(e.target.value)}
                    className="h-9 bg-background shadow-sm border-muted-foreground/20 focus:ring-primary/20"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="exclude-ranges" className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                    <Filter className="w-3 h-3" />
                    自定义排除区间
                  </Label>
                  <Input
                    id="exclude-ranges"
                    type="text"
                    placeholder="YYYY-MM-DD:YYYY-MM-DD"
                    value={excludeRangesStr}
                    onChange={(e) => setExcludeRangesStr(e.target.value)}
                    className="h-9 bg-background shadow-sm border-muted-foreground/20 placeholder:text-muted-foreground/40 focus:ring-primary/20"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 滚动区域：Markdown 内容 */}
          <div className="flex-1 overflow-y-auto p-8 min-h-0 bg-white/50">
            <div className="prose prose-sm max-w-none 
              [&_blockquote_p]:before:content-none [&_blockquote_p]:after:content-none 
              [&_table]:border-collapse [&_table]:w-full [&_table]:mb-8
              [&_thead]:bg-muted/50 [&_th]:px-4 [&_th]:py-2.5 [&_th]:border [&_th]:text-left [&_th]:font-semibold
              [&_td]:px-4 [&_td]:py-2 [&_td]:border [&_tr:nth-child(even)]:bg-muted/5
              [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-6 [&_h1]:pb-2 [&_h1]:border-b
              [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:mt-8 [&_h2]:mb-4
              [&_h3]:text-lg [&_h3]:font-medium [&_h3]:mt-6 [&_h3]:mb-3
              [&_blockquote]:border-l-4 [&_blockquote]:border-primary/20 [&_blockquote]:bg-primary/5 [&_blockquote]:px-4 [&_blockquote]:py-1 [&_blockquote]:rounded-r-md">
              {markdownReport ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {markdownReport}
                </ReactMarkdown>
              ) : (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                  <div className="w-8 h-8 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
                  <div className="text-sm text-muted-foreground animate-pulse font-medium">
                    正在实时计算分析报告...
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 底部固定区域：操作按钮 */}
          <div className="px-6 py-4 border-t flex justify-end gap-3 shrink-0 bg-background shadow-[0_-4px_12px_rgba(0,0,0,0.03)]">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyMarkdown}
              disabled={!markdownReport}
              className="px-6 h-9 transition-all hover:bg-muted"
            >
              复制 Markdown
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="px-6 h-9"
            >
              关闭面板
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}

